import type { HttpClient } from '~/api/client';
import { httpClient } from '~/api/client';
import type { OAISchema } from '~/types/openapi';

type CancellableProductOrderResponse =
  OAISchema['MarketCancellableProductOrderResponse'];
type OrderClaimResponse = OAISchema['MarketOrderClaimResponse'];
type ProductOrderResponse = OAISchema['MarketProductOrderResponse'];
type ClaimReasonResponse = OAISchema['ClaimReasonResponse'];
type ClaimDetailResponse = OAISchema['MarketClaimResponse'];
type CreateClaimResponse = OAISchema['MarketCreateClaimResponse'];

type ClaimItem = OAISchema['MarketClaimResponse'];
type CursorPagedResponse = OAISchema['CursorPagedResponse'];

// 교환/반품 목록 페이지네이션 응답 타입 (content를 구체화)
export type ClaimHistoryPageResponse = Omit<CursorPagedResponse, 'content'> & {
  content: ClaimItem[];
};

export type CancelProductOrdersRequestBody =
  OAISchema['MarketCancelProductOrdersRequest'];

export interface ClaimDataSource {
  getClaims(params?: {
    lastOfferId?: number;
    size?: number;
  }): Promise<ClaimHistoryPageResponse>;
  getCancellableProductOrders(
    orderId: number,
  ): Promise<CancellableProductOrderResponse>;
  getCanceledProductDetail(productOrderId: number): Promise<OrderClaimResponse>;
  cancelProductOrders(data: CancelProductOrdersRequestBody): Promise<void>;
  getClaimableProductOrders(
    vendorOrderId: number,
  ): Promise<ProductOrderResponse[]>;
  getClaimReasons(): Promise<ClaimReasonResponse[]>;
  getClaimDetail(claimId: number): Promise<ClaimDetailResponse>;
  createClaim(formData: FormData): Promise<CreateClaimResponse>;
}

export class ClaimDataSourceImpl implements ClaimDataSource {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly client: HttpClient = httpClient) {}

  async getClaims(params?: { lastOfferId?: number; size?: number }) {
    return this.client.request<ClaimHistoryPageResponse>({
      url: '/api/market-place/v1/claim',
      method: 'get',
      params,
    });
  }

  async getCancellableProductOrders(orderId: number) {
    return this.client.request<CancellableProductOrderResponse>({
      url: `/api/market-place/v2/orders/${orderId}/cancellable-product-orders`,
      method: 'get',
    });
  }

  async getCanceledProductDetail(productOrderId: number) {
    return this.client.request<OrderClaimResponse>({
      url: `/api/market-place/v1/claim/cancellation/${productOrderId}/detail`,
      method: 'get',
    });
  }

  async cancelProductOrders(data: CancelProductOrdersRequestBody) {
    await this.client.request<unknown>({
      url: '/api/market-place/v2/claim/kpn/cancel-product-orders',
      method: 'post',
      data,
    });
  }

  async getClaimableProductOrders(vendorOrderId: number) {
    return this.client.request<ProductOrderResponse[]>({
      url: `/api/market-place/v1/orders/${vendorOrderId}/claimable-product-orders`,
      method: 'get',
    });
  }

  async getClaimReasons() {
    return this.client.request<ClaimReasonResponse[]>({
      url: '/api/market-place/v1/claim/reason',
      method: 'get',
    });
  }

  async getClaimDetail(claimId: number) {
    return this.client.request<ClaimDetailResponse>({
      url: `/api/market-place/v1/claim/${claimId}/detail`,
      method: 'get',
    });
  }

  async createClaim(formData: FormData) {
    return this.client.request<CreateClaimResponse>({
      url: '/api/market-place/v1/claim',
      method: 'post',
      data: formData,
    });
  }
}

export const claimDataSource = new ClaimDataSourceImpl();
